import {
  Box,
  Button,
  Card,
  Chip,
  IconButton,
  MenuItem,
  Radio,
  Select,
  Skeleton,
  Slider,
  TableCell,
  Typography,
} from "@mui/material";
import ShoppingCartCheckoutIcon from "@mui/icons-material/ShoppingCartCheckout";
import ColumnContainer from "../../../common/ColumnContainer";
import ModalComponent from "../../../common/ModalComponent";
import RowContainer from "../../../common/RowContainer";
import { DashboardButton } from "../../../common/DashboardButton";
import { AddRounded, RemoveRounded } from "@mui/icons-material";
import { useEffect, useState } from "react";
import { useUpdateSingleUserMutation } from "../../../../redux/features/usersManagement/usersManagementApi";
import {
  StyledTable,
  TableWrapper,
  TitleContainer,
  Wrapper,
} from "../../style";
import "./CreditsStyle.less";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { Link, useNavigate } from "react-router-dom";
import { userDashboardRoute } from "../../../../utils/routes/Routes";
import { useDispatch, useSelector } from "react-redux";
import { selectAuthData } from "../../../../redux/features/auth/authSlice";
import { useCreateNewOrderMutation } from "../../../../redux/features/orders/orderApi";
import {
  selectOrder,
  setOrderDetails,
} from "../../../../redux/features/orders/orderSlice";
import { selectStep } from "../../../../redux/features/credits/creditsSlice";
import { useGetCreditPricingTiersQuery } from "../../../../redux/features/credits/creditsApi";
import { FormattedMessage } from "react-intl";

const BuyCreditsPage = () => {
  // INIT
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const currentOrder = useSelector(selectOrder);
  // STATE
  const [sliderCount, setSliderCount] = useState(currentOrder.credits || 0);

  const [specialCredits, setSpecialCredits] = useState([]);
  const [maxSliderValue, setMaxSliderValue] = useState(0);

  useEffect(() => {
    if (currentOrder.credits !== undefined) {
      setSliderCount(currentOrder.credits);
    }
  }, [currentOrder.credits]);

  // REDUX
  const userData = useSelector(selectAuthData);
  const adminIdAssociatedToUser = userData?.userData?.admin_id;
  const { data: pricingCredits, isLoading: isLoadingPricing } =
    useGetCreditPricingTiersQuery({
      adminId: adminIdAssociatedToUser,
    });

  const [updateUser, { isLoading }] = useUpdateSingleUserMutation();

  const [createNewOrder, { isLoading: isLoadingOrder, error: isErrorOrder }] =
    useCreateNewOrderMutation();
  const step = useSelector(selectStep);

  // EFFECTS
  useEffect(() => {
    const special = pricingCredits?.filter((item) => item.is_special);
    setSpecialCredits(special);
  }, [pricingCredits]);

  useEffect(() => {
    const maxSliderValue =
      pricingCredits && pricingCredits[pricingCredits?.length - 1]?.max_tier;
    setMaxSliderValue(maxSliderValue);
  }, [pricingCredits]);

  // FUNCTIONS
  const handleChangeSlider = (e) => {
    setSliderCount(e.target.value);

    dispatch(
      setOrderDetails({
        credits: e.target.value,
        orderAmount: calculatePrice(e.target.value),
      })
    );
  };

  const calculatePrice = (credits) => {
    const tier = pricingCredits?.find(
      (tier) => credits >= tier.min_tier && credits <= tier.max_tier
    );
    if (!tier) return null;

    const basePrice = credits * tier.unit_price;
    return basePrice;
  };

  const specialMarks = pricingCredits
    ?.filter((item) => item.is_special)
    ?.map((item) => ({
      value: item.min_tier,
      label: item.min_tier,
    }));

  const handleConfirm = async () => {
    const orderData = {
      order_amount: currentOrder.orderAmount,
    };

    try {
      const result = await createNewOrder({
        order_details: orderData,
      });
      if (result?.data?.id !== currentOrder.orderId) {
        dispatch(
          setOrderDetails({
            ...currentOrder,
            orderId: result?.data?.id,
          })
        );
      } else {
        dispatch(
          setOrderDetails({
            orderId: currentOrder.orderId,
            orderAmount: currentOrder.orderAmount,
            credits: currentOrder.credits,
          })
        );
      }

      navigate(userDashboardRoute.buyCreditsCheckout);
    } catch (error) {
      console.error("Error creating order", error);
    }
  };

  //UTILS

  return (
    <Wrapper className="buy-credits-page">
      <ColumnContainer style={{ height: "100vh" }}>
        {isLoading ? (
          <Skeleton variant="rectangular" width="100%" height="100%" />
        ) : (
          <ColumnContainer halfGap sx={{ gap: "40px" }}>
            <ColumnContainer style={{ gap: "70px" }}>
              <RowContainer>
                <TitleContainer>
                  <Typography variant="h4" component="h4">
                    <FormattedMessage id="credits.title" />
                  </Typography>
                </TitleContainer>
              </RowContainer>
              <ColumnContainer>
                <ColumnContainer halfGap>
                  <Typography variant="h6" component="h6">
                    <FormattedMessage id="credits.prices" />
                  </Typography>
                  <ColumnContainer className="credits-boxes">
                    <TableWrapper>
                      <StyledTable>
                        <thead>
                          <tr>
                            <th>
                              <FormattedMessage id="credits.tiers" />
                            </th>
                            <th>
                              <FormattedMessage id="credits.unitPrice" />
                            </th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {pricingCredits?.map((tier) => (
                            <tr key={tier.id}>
                              <TableCell>
                                {tier.min_tier === tier.max_tier
                                  ? tier.min_tier
                                  : `${tier.min_tier} - ${tier.max_tier}`}
                              </TableCell>

                              <TableCell>
                                € {tier?.unit_price?.toFixed(2)}
                              </TableCell>
                              <TableCell sx={{ textAlign: "center" }}>
                                {tier.is_special ? (
                                  <Chip
                                    icon={<AutoAwesomeIcon fontSize="small" />}
                                    label={
                                      <Typography variant="body2" component="p">
                                        <FormattedMessage id="credits.specialPrice" />
                                      </Typography>
                                    }
                                  ></Chip>
                                ) : null}
                              </TableCell>
                            </tr>
                          ))}
                        </tbody>
                      </StyledTable>
                    </TableWrapper>
                  </ColumnContainer>
                </ColumnContainer>
              </ColumnContainer>
              <ColumnContainer>
                <Typography variant="h6" component="h6">
                  <FormattedMessage id="credits.bar" />
                </Typography>
                <Slider
                  onChange={(e) => handleChangeSlider(e)}
                  className="credits-slider"
                  color={"warning"}
                  value={sliderCount || currentOrder.credits}
                  min={0}
                  max={maxSliderValue}
                  step={step}
                  // marks={specialMarks}
                  valueLabelDisplay="on"
                />
              </ColumnContainer>
            </ColumnContainer>
            <ColumnContainer>
              <Card className="checkout-card">
                <ColumnContainer>
                  <RowContainer>
                    <Typography variant="h6" component="h6">
                      <FormattedMessage id="credits.selected" />
                    </Typography>
                    <Typography variant="h6" component="h6">
                      <FormattedMessage id="credits.total" />
                    </Typography>
                  </RowContainer>
                  <RowContainer>
                    <Typography
                      variant="body2"
                      component="p"
                      fontSize={"18px"}
                      color={sliderCount === 0 ? "red" : "var(--text-primary)"}
                    >
                      {sliderCount} <FormattedMessage id="credits" />
                    </Typography>

                    <RowContainer align="center" end>
                      {sliderCount &&
                      specialCredits?.find(
                        (sp) =>
                          sp.min_tier <= sliderCount &&
                          sp.max_tier >= sliderCount
                      ) ? (
                        <Chip
                          icon={<AutoAwesomeIcon fontSize="small" />}
                          label={
                            <Typography variant="body2" component="p">
                              <FormattedMessage id="credits.specialPrice" />
                            </Typography>
                          }
                        ></Chip>
                      ) : null}
                      {sliderCount ? (
                        <Typography
                          variant="body1"
                          component="p"
                          fontSize={"18px"}
                        >
                          {calculatePrice(sliderCount)}€
                        </Typography>
                      ) : null}
                    </RowContainer>
                  </RowContainer>
                </ColumnContainer>
              </Card>
              <RowContainer end style={{ marginBottom: "20px" }}>
                <DashboardButton
                  type="filled"
                  className="checkout-btn"
                  disabled={sliderCount === 0}
                >
                  <RowContainer
                    center
                    align="center"
                    minGap
                    onClick={handleConfirm}
                  >
                    <FormattedMessage id="button.confirm" />
                    <ShoppingCartCheckoutIcon />
                  </RowContainer>
                </DashboardButton>
              </RowContainer>
            </ColumnContainer>
          </ColumnContainer>
        )}
      </ColumnContainer>
    </Wrapper>
  );
};
export default BuyCreditsPage;
